// Dismantling & Compliance API calls. Role and institution come from the token on the API side,
// so none are sent here. Errors are { errorMsg, code, details? }.
import axios from "axios";
import config from "@/config";
import getRequest from "@/api/getRequest";
import patchRequest from "@/api/patchRequest";
import postRequest from "@/api/postRequest";
import putRequest from "@/api/putRequest";

// One commandId per user action; reuse it when the same action is retried.
export function newCommandId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // eslint-disable-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // eslint-disable-line no-bitwise
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Resolves { dossier, revision, replayed }; 201 = started, 200 = existing dossier.
export function startDossier(carId, commandId) {
  return postRequest
    .object("/compliance/dossiers", { carId, commandId })
    .then((res) => res.data);
}

// Resolves { dossier, revision, car, mismatch, files }.
export function getDossier(dossierId) {
  return getRequest(`/compliance/dossiers/${encodeURIComponent(dossierId)}`);
}

// Draft save. `set` is a nested partial dossier with only the changed fields.
// Resolves { dossier, revision, car, mismatch, files, validation, replayed, unchanged? }.
// Rejects with the axios error: 409 revision_conflict, 422 invalid_input { field, reason }.
export function patchDossier(dossierId, revision, commandId, set) {
  return patchRequest(
    `/compliance/dossiers/${encodeURIComponent(dossierId)}`,
    "",
    { revision, commandId, set },
  ).then((res) => res.data);
}

// Confirms the acceptance and stores its deadlines. Resolves the same view as patchDossier, plus
// replayed. Rejects with the axios error: 409 revision_conflict, 409 already_confirmed,
// 422 confirm_blocked { details: { blocking, warnings } }.
export function confirmDossier(dossierId, revision, commandId) {
  return postRequest
    .object(`/compliance/dossiers/${encodeURIComponent(dossierId)}/confirm`, {
      revision,
      commandId,
    })
    .then((res) => res.data);
}

// Append-only weighing. `body` is { method, weighedAt, gross, tare, ticketNumber } or
// { method, weighedAt, net, device }. Resolves the same view as patchDossier, plus replayed.
// Rejects with the axios error: 409 revision_conflict, 422 invalid_input { field, reason }.
export function addWeighing(dossierId, revision, commandId, body) {
  return postRequest
    .object(`/compliance/dossiers/${encodeURIComponent(dossierId)}/weighings`, {
      ...body,
      revision,
      commandId,
    })
    .then((res) => res.data);
}

// Append-only private file. `target` is { purpose: "photo", photoKind } or
// { purpose: "checklist", checklistCode }. Resolves the same view as patchDossier, plus replayed
// and fileId. Rejects with the axios error: 409 revision_conflict, 413 file_too_large,
// 415 unsupported_type, 422 invalid_input { field, reason }.
export function uploadDossierFile(
  dossierId,
  revision,
  commandId,
  target,
  file,
) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  data.append("purpose", target.purpose);
  if (target.photoKind) data.append("photoKind", target.photoKind);
  if (target.checklistCode) data.append("checklistCode", target.checklistCode);
  data.append("file", file, file.name);
  return postRequest
    .formData(
      `/compliance/dossiers/${encodeURIComponent(dossierId)}/files`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Removes a file and deletes its bytes. Resolves the same view as patchDossier, plus replayed.
// `reason` is required after confirm (a manager only) and ignored while a draft.
// Rejects with the axios error: 403 not_uploader (draft) or not_manager (confirmed), 404 not_found,
// 409 revision_conflict, 422 reason_required.
// axios directly: deleteRequest sends no body.
export function removeDossierFile(
  dossierId,
  fileId,
  revision,
  commandId,
  reason,
) {
  return axios
    .delete(
      `${config.getServerUri()}/compliance/dossiers/${encodeURIComponent(dossierId)}/files/${encodeURIComponent(fileId)}`,
      {
        headers: { authorization: localStorage.token },
        data: { revision, commandId, reason },
      },
    )
    .then((res) => res.data);
}

// Corrects a frozen field group of a confirmed acceptance (Manager / Admin). `body` is
// { group, value, reason } with `dryRun: true` for a preview.
// Resolves the same view as patchDossier plus replayed and warnings [{ code }], or for a dryRun
// { dryRun, revision, warnings, deadlinesPreview, changes } with nothing saved.
// Rejects with the axios error: 403 not_manager, 409 revision_conflict / not_confirmed,
// 422 not_correctable / reason_required / arrival_after_confirm_day / correction_blocked
// { details: { blocking } } / invalid_input { details: { field, reason } }.
export function correctDossier(dossierId, revision, commandId, body) {
  return postRequest
    .object(
      `/compliance/dossiers/${encodeURIComponent(dossierId)}/corrections`,
      { ...body, revision, commandId },
    )
    .then((res) => res.data);
}

// A private dossier file as an object URL; the caller revokes it. The token goes in the header,
// so the file URL alone gives 401.
export function getFileBlob(fileId) {
  return axios
    .get(
      `${config.getServerUri()}/compliance/files/${encodeURIComponent(fileId)}`,
      {
        headers: { authorization: localStorage.token },
        responseType: "blob",
      },
    )
    .then((res) => URL.createObjectURL(res.data));
}

// Resolves { events, count, page, limit }, newest first.
export function getDossierEvents(dossierId, page = 1, limit = 50) {
  return getRequest(
    `/compliance/dossiers/${encodeURIComponent(dossierId)}/events`,
    `page=${page}&limit=${limit}`,
  );
}

// The PRA case of a dossier (Stage 2). Resolves { praCase, revision, nextStep, dueDate, late,
// warnings, files, carPra }. Rejects with the axios error: 404 pra_case_not_found (no case yet),
// 404 not_found, 404 module_disabled (PRA off).
export function getPraCase(dossierId) {
  return getRequest(
    `/compliance/dossiers/${encodeURIComponent(dossierId)}/pra-case`,
  );
}

// Workshop depollution checklist (Stage 3a): the checklist view of a dossier, or 404 no_checklist
// when it is not open yet (a draft, or the backfill has not run).
export function getWorkshopChecklistByDossier(dossierId) {
  return getRequest(
    `/compliance/dossiers/${encodeURIComponent(dossierId)}/workshop-checklist`,
  );
}

export function getWorkshopChecklist(checklistId) {
  return getRequest(
    `/compliance/workshop/checklists/${encodeURIComponent(checklistId)}`,
  );
}

// Records one task result (multipart). `result` is workshopForm's resultPayload: { taskCode,
// result, reason?, quantityValue?, quantityUnit?, quantityBasis?, container?, performedAt?,
// files? } (files: File objects, sent as "files"). Sends the checklist revision, not the
// dossier's. Resolves the checklist view plus revision and replayed (201 recorded, 200 replay).
// Rejects with the axios error: 409 revision_conflict / result_exists, 413 upload_too_large
// (details.reason too_many_files | total_size) / file_too_large, 415 unsupported_type, 422 with
// details.field (unknown_task, reason_required, quantity_*, basis_required, container_required,
// performed_at_*, evidence_required, invalid_input).
export function recordWorkshopResult(checklistId, revision, commandId, result) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  Object.entries(result).forEach(([field, value]) => {
    // Evidence files: one "files" part each (at most 3, 15 MB in total).
    if (Array.isArray(value)) value.forEach((item) => data.append(field, item));
    else if (value !== undefined && value !== null) data.append(field, value);
  });
  return postRequest
    .formData(
      `/compliance/workshop/checklists/${encodeURIComponent(checklistId)}/results`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Accepts a current exception (Manager / Admin; the API checks the role). note is optional.
// Resolves the checklist view plus revision and replayed. Rejects with the axios error:
// 403 not_manager, 409 revision_conflict / not_current / already_accepted / checklist_approved,
// 422 not_an_exception / invalid_input.
export function acceptWorkshopException(
  checklistId,
  resultId,
  revision,
  commandId,
  note,
) {
  return postRequest
    .object(
      `/compliance/workshop/checklists/${encodeURIComponent(checklistId)}/results/${encodeURIComponent(resultId)}/accept-exception`,
      { revision, commandId, note: note || undefined },
    )
    .then((res) => res.data);
}

// Approves the checklist (Manager / Admin). Resolves the checklist view plus revision and
// replayed. Rejects with the axios error: 403 not_manager, 409 revision_conflict /
// already_approved, 422 approval_blocked { details: { blocking } }.
export function approveWorkshopChecklist(checklistId, revision, commandId) {
  return postRequest
    .object(
      `/compliance/workshop/checklists/${encodeURIComponent(checklistId)}/approve`,
      { revision, commandId },
    )
    .then((res) => res.data);
}

// Corrects one field of a current result (Manager / Admin, before and after approval). body is
// { resultId, field, value, reason?, dryRun? }; field quantity ({ value, unit, basis } or null),
// container, reason (text) or performedAt (ISO). Resolves the checklist view plus revision,
// replayed and warnings, or with dryRun { dryRun: true, revision, warnings, changes }. Rejects
// with the axios error: 403 not_manager, 409 revision_conflict / result_superseded,
// 422 not_correctable / reason_required / invalid_input (no_change) and the recording codes for
// the new value (details.field "value").
export function correctWorkshopResult(checklistId, revision, commandId, body) {
  return postRequest
    .object(
      `/compliance/workshop/checklists/${encodeURIComponent(checklistId)}/corrections`,
      { revision, commandId, ...body },
    )
    .then((res) => res.data);
}

// Workshop home. state: open (default), to_approve, pending_exceptions, overdue, approved, all;
// q matches the plate or the VIN. Resolves { checklists: [{ checklistId, car: { brand, model,
// targa, vin }, state, done, total, pendingExceptions, deadline }], count, counts, page, limit }.
// counts has open, to_approve, pending_exceptions, overdue and approved for the same q.
export function listWorkshopChecklists({
  q = "",
  state = "open",
  page = 1,
  limit = 50,
} = {}) {
  const params = new URLSearchParams({
    state,
    page: String(page),
    limit: String(limit),
  });
  if (q) params.set("q", q);
  return getRequest("/compliance/workshop/checklists", params.toString());
}

// Scanner: the car QR is the car id. Resolves { checklistId }. Rejects with the axios error: 404
// car_not_found, no_dossier, dossier_not_confirmed (details.dossierId), no_checklist,
// module_disabled.
export function getWorkshopByCar(carId) {
  return getRequest(`/compliance/workshop/by-car/${encodeURIComponent(carId)}`);
}

// Resolves { dossiers: [{ carId, dossierId, status, deadlines }] } (at most 50 ids). With PRA on,
// each row also has pra: { state, onHold, late, lateAtRecording } | null.
export function getDossiersByCars(carIds) {
  return getRequest(
    "/compliance/dossiers/by-cars",
    `carIds=${encodeURIComponent(carIds.join(","))}`,
  );
}

// Records the CRD of a PRA case (multipart; `crd` is { number, issuedOn, file }). Sends the case
// revision, not the dossier's. Resolves the same view as getPraCase plus replayed and fileId
// (201 recorded, 200 replay). Rejects with the axios error: 409 revision_conflict /
// crd_number_in_use / step_out_of_order / already_confirmed / case_on_hold, 413 file_too_large,
// 415 unsupported_type, 422 invalid_input / file_required / date_in_future / date_order
// { details: { field, conflictsWith } }.
export function recordPraCrd(caseId, revision, commandId, crd) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  data.append("number", crd.number);
  data.append("issuedOn", crd.issuedOn);
  data.append("file", crd.file, crd.file.name);
  return postRequest
    .formData(
      `/compliance/pra-cases/${encodeURIComponent(caseId)}/crd`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Records the deregistration request of a PRA case (multipart; `request` is { date, channel,
// agencyName?, file? }: the agency only for sta_agency, the file optional). Sends
// the case revision. Resolves the same view as getPraCase plus replayed, fileId (null without a
// file) and warnings [{ code: "request_late", dueDate }] when dated after the due date (201
// recorded, 200 replay). Rejects with the axios error: 409 revision_conflict / step_out_of_order /
// already_confirmed / case_on_hold, 413 file_too_large, 415 unsupported_type, 422 invalid_input
// { details: { field } } / date_in_future / date_order.
export function recordPraRequest(caseId, revision, commandId, request) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  data.append("date", request.date);
  data.append("channel", request.channel);
  if (request.agencyName) data.append("agencyName", request.agencyName);
  if (request.file) data.append("file", request.file, request.file.name);
  return postRequest
    .formData(
      `/compliance/pra-cases/${encodeURIComponent(caseId)}/request`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Records the deregistration receipt of a PRA case and closes it as confirmed (multipart; `receipt`
// is { number, date, file }, all required). Sends the case revision. Resolves the same view as
// getPraCase plus replayed and fileId (201 recorded, 200 replay). Rejects with the axios error: 409
// revision_conflict / step_out_of_order / already_confirmed / case_on_hold, 413 file_too_large,
// 415 unsupported_type, 422 invalid_input / file_required / date_in_future / date_order.
export function recordPraReceipt(caseId, revision, commandId, receipt) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  data.append("number", receipt.number);
  data.append("date", receipt.date);
  data.append("file", receipt.file, receipt.file.name);
  return postRequest
    .formData(
      `/compliance/pra-cases/${encodeURIComponent(caseId)}/receipt`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Puts a PRA case on hold (multipart; `hold` is { type, note, file? }). Sends the case revision. The
// state does not change, and no step can be recorded until the release. Resolves the same view as
// getPraCase plus replayed and fileId (null without a file) (201 placed, 200 replay). Rejects with
// the axios error: 409 revision_conflict / already_on_hold / already_confirmed, 413 file_too_large,
// 415 unsupported_type, 422 invalid_input { details: { field } }.
export function placePraHold(caseId, revision, commandId, hold) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  data.append("type", hold.type);
  data.append("note", hold.note);
  if (hold.file) data.append("file", hold.file, hold.file.name);
  return postRequest
    .formData(
      `/compliance/pra-cases/${encodeURIComponent(caseId)}/holds`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Releases the hold of a PRA case with a reason (at most 500 characters). Sends the case revision.
// The state does not change. Resolves the same view as getPraCase plus replayed. Rejects with the
// axios error: 409 revision_conflict / not_on_hold, 422 reason_required.
export function releasePraHold(caseId, revision, commandId, reason) {
  return postRequest
    .object(
      `/compliance/pra-cases/${encodeURIComponent(caseId)}/holds/release`,
      { revision, commandId, reason },
    )
    .then((res) => res.data);
}

// Corrects one recorded value of a PRA case (Manager / Admin): body { group, value, reason,
// dryRun? }; value is one value, or { channel, agencyName } for request.channel. Sends the case
// revision. Resolves the same view as getPraCase plus replayed and warnings, or with dryRun
// { dryRun, revision, warnings, changes } (nothing saved). Rejects with the axios error: 403
// not_manager, 409 revision_conflict / crd_number_in_use, 422 not_correctable / reason_required /
// invalid_input (no_change) / date_in_future / date_order.
export function correctPraCase(caseId, revision, commandId, body) {
  return postRequest
    .object(`/compliance/pra-cases/${encodeURIComponent(caseId)}/corrections`, {
      ...body,
      revision,
      commandId,
    })
    .then((res) => res.data);
}

// Removes an optional PRA file (the request's or a hold's). Sends the case revision. Resolves
// the same view as getPraCase plus replayed. Rejects with the axios error: 403 not_uploader /
// not_manager, 404 not_found, 409 revision_conflict, 422 reason_required / file_required (a
// required document).
export function removePraFile(caseId, fileId, revision, commandId, reason) {
  return axios
    .delete(
      `${config.getServerUri()}/compliance/pra-cases/${encodeURIComponent(caseId)}/files/${encodeURIComponent(fileId)}`,
      {
        headers: { authorization: localStorage.token },
        data: { revision, commandId, reason },
      },
    )
    .then((res) => res.data);
}

// Replaces a PRA file (multipart; `change` is { file, reason? }). Sends the case revision. Resolves
// the same view as getPraCase plus replayed and fileId (the new file). Rejects with the axios error:
// 403 not_uploader / not_manager, 404 not_found, 409 revision_conflict, 413 file_too_large,
// 415 unsupported_type, 422 reason_required / file_required.
export function replacePraFile(caseId, fileId, revision, commandId, change) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  if (change.reason) data.append("reason", change.reason);
  data.append("file", change.file, change.file.name);
  return postRequest
    .formData(
      `/compliance/pra-cases/${encodeURIComponent(caseId)}/files/${encodeURIComponent(fileId)}/replace`,
      "",
      data,
    )
    .then((res) => res.data);
}

// Per-institution switches (SuperAdmin, institution page).
// Resolves { enabled, praEnabled, workshopEnabled, wasteEnabled, revision, changedAt,
// praChangedAt, workshopChangedAt, wasteChangedAt, globalEnabled }.
export function getInstitutionSwitch(institutionId) {
  return getRequest(
    `/institution/${encodeURIComponent(institutionId)}/dismantling-compliance`,
  );
}

// stage "compliance" | "pra" | "workshop" | "waste". Resolves the state above plus { changed,
// backfill, setup } (setup: { listVersion, codesInserted, codesTotal, areasCreated } after waste
// ON); 409 revision_conflict / compliance_off / workshop_template_missing / workshop_off,
// 422 invalid_input.
export function setInstitutionSwitch(institutionId, change) {
  return putRequest(
    `/institution/${encodeURIComponent(institutionId)}/dismantling-compliance`,
    "",
    change,
  ).then((res) => res.data);
}

// --- Waste containers (Stage 6). Every call answers 404 module_disabled while waste is off. ---

// Resolves { codes: [{ id, code, codeKey, description, hazardous, hp, validFrom, validTo, source,
// suggestedTasks, mirrorOf, verified, provisional }], count, listVersion, provisional }.
export function getWasteCodes() {
  return getRequest("/compliance/waste/codes");
}

// filters { view: all | not_weighed, code, q, status: all | active | inactive }. Resolves { groups:
// [{ code, codeKey, description, hazardous, totals: { stock: { l, kg, bodies, weighedKg },
// sentOutThisMonthKg }, containers: [row] }], count, counts: { all, not_weighed }, provisional,
// canCreate }. A row is { id, name, type, unit, status, recorded: { value, unit, vehicles, bodies },
// latestWeighing, lastSendOut }.
export function listWasteContainers(filters = {}) {
  const params = Object.entries(filters)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return getRequest("/compliance/waste/containers", params);
}

// Resolves { container, revision, canEdit, codeLocked, entries: { items, page, pageSize, total,
// hasMore }, timeline }: the current filling's entries, newest first. An item is { id, sign,
// reversal, reversed, quantity: { value, unit, basis }, taskCode, dossierId, carId, vehicle: {
// targa, vin, brand, model } | null, at, recordedAt, recordedBy }. container.latestWeighing is
// { id, netKg, weighedAt, fresh } | null (fresh: taken after the last entry, usable for a
// send-out). timeline: weighings ({ kind: "weighing", id, method, gross, tare, net, ticketNumber,
// device, weighedAt, by, current }) and send-outs ({ kind: "sendOut", id, date, weightKg,
// recipient, transporter, file: { id, originalName, mimetype } | null, content: { value, unit,
// bodies, entryCount }, by }), newest first.
export function getWasteContainer(containerId) {
  return getRequest(
    `/compliance/waste/containers/${encodeURIComponent(containerId)}`,
  );
}

// Further pages of the current filling's entries. Resolves { entries, revision }.
export function getWasteContainerEntries(containerId, page) {
  return getRequest(
    `/compliance/waste/containers/${encodeURIComponent(containerId)}/entries`,
    `page=${encodeURIComponent(page)}`,
  );
}

// The containers a depollution result can fill: active, not an area, suggested for taskCode
// first. Resolves { containers: [{ id, name, type, unit, code, codeKey, description, hazardous,
// suggested }] }.
export function pickWasteContainers({ taskCode = "", unit = "" } = {}) {
  const params = new URLSearchParams();
  if (taskCode) params.set("taskCode", taskCode);
  if (unit) params.set("unit", unit);
  return getRequest("/compliance/waste/containers/pick", params.toString());
}

// Manager / Admin. body { name, type, unit, tare, note, wasteCodeId }. Resolves the detail view
// plus replayed (201 created, 200 replay). Rejects with the axios error: 403 not_manager, 409
// name_taken, 422 invalid_input { details: { field } } / unknown_code.
export function createWasteContainer(commandId, body) {
  return postRequest
    .object("/compliance/waste/containers", { ...body, commandId })
    .then((res) => res.data);
}

// Manager / Admin. `set` holds only the changed fields of { name, type, unit, tare, note, status,
// wasteCodeId }. Resolves the detail view plus replayed. Rejects with the axios error: 403
// not_manager, 409 revision_conflict / name_taken / code_locked, 422 invalid_input / no_change /
// unknown_code.
export function updateWasteContainer(containerId, revision, commandId, set) {
  return patchRequest(
    `/compliance/waste/containers/${encodeURIComponent(containerId)}`,
    "",
    { revision, commandId, set },
  ).then((res) => res.data);
}

// Any role, also an inactive container. `body` is { method, weighedAt, gross, tare, ticketNumber }
// or { method, weighedAt, net, device }; a missing tare is the container's. Resolves the detail
// view plus replayed and weighingId (201 recorded, 200 replay). Rejects with the axios error: 409
// revision_conflict, 422 invalid_input { details: { field, reason } }.
export function addWasteWeighing(containerId, revision, commandId, body) {
  return postRequest
    .object(
      `/compliance/waste/containers/${encodeURIComponent(containerId)}/weighings`,
      { ...body, revision, commandId },
    )
    .then((res) => res.data);
}

// Manager / Admin. Multipart; `sendOut` is sendOutForm's buildSendOut: { date, recipient,
// transporter?, weighingId | weighing, file? }. The inline weighing goes as JSON text. Resolves the
// detail view plus replayed and sendOutId (201 recorded, 200 replay); the container starts a new,
// empty filling. Rejects with the axios error: 403 not_manager, 409 revision_conflict, 413
// file_too_large, 415 unsupported_type, 422 recipient_required / weight_required / weighing_stale
// / invalid_input { details: { field, reason } }.
export function recordWasteSendOut(containerId, revision, commandId, sendOut) {
  const data = new FormData();
  data.append("revision", String(revision));
  data.append("commandId", commandId);
  data.append("date", sendOut.date);
  data.append("recipient", sendOut.recipient);
  if (sendOut.transporter) data.append("transporter", sendOut.transporter);
  if (sendOut.weighingId) data.append("weighingId", sendOut.weighingId);
  if (sendOut.weighing)
    data.append("weighing", JSON.stringify(sendOut.weighing));
  if (sendOut.file) data.append("file", sendOut.file, sendOut.file.name);
  return postRequest
    .formData(
      `/compliance/waste/containers/${encodeURIComponent(containerId)}/send-outs`,
      "",
      data,
    )
    .then((res) => res.data);
}

// --- Car-body release (Stage 6 slice 4). 404 module_disabled while waste is off. ---

// Resolves { release: { releasedAt, releasedBy: { name }, container: { id, name }, weightKg,
// partsCountAtRelease, confirmedPartsRemoved, confirmedNoHazards, sentOut, canCancel } | null,
// revision, canRelease, blocking: [depollution_not_approved | already_released | no_area],
// areas: [{ id, name, bodies, kg }], partsCount (live), approvedAt, history: [{ action, at, by,
// containerId, reason }] }.
export function getCarBodyRelease(dossierId) {
  return getRequest(
    `/compliance/dossiers/${encodeURIComponent(dossierId)}/car-body-release`,
  );
}

// Manager / Admin. body { containerId, weightKg?, confirmPartsRemoved: true, confirmNoHazards:
// true }. Resolves the view above plus replayed (201 released, 200 replay). Rejects with the axios
// error: 403 not_manager, 409 depollution_not_approved / already_released / revision_conflict,
// 422 confirmation_required { details: { field } } / container_not_allowed / invalid_input.
export function releaseCarBody(dossierId, revision, commandId, body) {
  return postRequest
    .object(
      `/compliance/dossiers/${encodeURIComponent(dossierId)}/car-body-release`,
      { ...body, revision, commandId },
    )
    .then((res) => res.data);
}

// Manager / Admin, with a reason, while the area is not sent out. Resolves the view above plus
// replayed. Rejects with the axios error: 403 not_manager, 409 not_released / already_sent_out /
// revision_conflict, 422 reason_required / invalid_input.
export function cancelCarBodyRelease(dossierId, revision, commandId, reason) {
  return postRequest
    .object(
      `/compliance/dossiers/${encodeURIComponent(dossierId)}/car-body-release/cancel`,
      { revision, commandId, reason },
    )
    .then((res) => res.data);
}
